// eslint-disable-next-line
import React, { useEffect, useState } from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import Loader from 'components/Loader';
import { RegexExpWithMsg } from 'utils/helpers';
import TextField from '@material-ui/core/TextField';
import { TextFieldProps } from 'formik-material-ui';
import { useFormikContext } from 'formik';
import {
  CustomEventProps,
  blockMaxTyping,
  blockPaste,
  blockTyping,
  customFieldToTextField,
  getMaxLength,
  isRequiredField,
} from '../helpers';

export type CustomTextFieldProps = {
  regex?: RegExp | RegexExpWithMsg;
  isLoading?: boolean;
  error?: string;
  showTypingErrors?: boolean;
  onReload?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly?: boolean;
  showErrorOnMount?: boolean;
} & TextFieldProps;

const CustomTextField: React.FC<CustomTextFieldProps> = ({
  form,
  field,
  regex,
  isLoading,
  showTypingErrors = true,
  onReload,
  error,
  readonly,
  ...rest
}) => {
  const formik = useFormikContext();
  const [temporalError, setTemporalError] = useState('');
  const [errMsgTime, setErrMsgTime] = useState(0);

  const [limit, setLimit] = useState(false);
  const required = isRequiredField(formik, field.name);
  // TO FIX: yup not updating schema using .when
  const maxLength =
    rest.inputProps?.maxLength ?? getMaxLength(formik, field.name);

  useEffect(() => () => clearTimeout(errMsgTime), [errMsgTime]);

  const handler = (
    e,
    fn: (...args) => void,
    params: RegExp | RegexExpWithMsg | number | undefined,
    showError?: boolean
  ) => {
    const eventProps: CustomEventProps = {
      form,
      field,
      temporalError,
      setTemporalError,
      errMsgTime,
      setErrMsgTime,
      limit,
      setLimit,
    };

    fn(e, params, eventProps, showError);
  };

  const onKeyPress = (
    e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (rest?.inputProps?.onKeyPress) {
      rest.inputProps.onKeyPress(e);
    }
    handler(e, blockTyping, regex);
  };

  const onKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (rest?.inputProps?.onKeyDown) {
      rest.inputProps.onKeyDown(e);
    }
    clearTimeout(errMsgTime);
    handler(e, blockMaxTyping, maxLength, showTypingErrors);
  };

  const onPaste = (
    e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (rest?.inputProps?.onPaste) {
      rest.inputProps.onPaste(e);
    }
    handler(e, blockPaste, regex);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    return false;
  };

  return (
    <TextField
      onDrop={onDrop}
      margin="dense"
      required={required}
      {...customFieldToTextField({
        form,
        field,
        temporalError,
        ...rest,
      })}
      InputLabelProps={{
        ...rest.InputLabelProps,
        shrink: true,
      }}
      InputProps={{
        ...rest.InputProps,
        ...(readonly && { readOnly: true, disabled: true }), // readonly styles
        endAdornment: isLoading ? (
          <InputAdornment position="end">
            <Loader loadingText="" inline />
          </InputAdornment>
        ) : (
          rest.InputProps?.endAdornment
        ),
        inputProps: {
          ...rest.inputProps,
          maxLength,
          onKeyPress,
          onKeyDown,
          onPaste,
        },
      }}
      onChange={rest?.onChange ? rest.onChange : field.onChange}
    />
  );
};

export default CustomTextField;
